import { ContactInfoFields } from './utils'
import { FC } from 'react'
import { InputMode } from '@dtx-company/shared-components/src'
import { InputProps } from './types'
import { MailOutlined, WebOutlined } from '@mui/icons-material'
import { Autocomplete, Stack, TextField } from '@dtx-company/design-system/src'
import { indexInterfaceWithVar } from '@dtx-company/true-common/src/utils/objects'
import {
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE
} from '@dtx-company/true-common/src/constants/countryCodes'
import { CountryCodeAutocomplete } from '@dtx-company/app/shared/code/src/components/myflowcodes/v2/CodeComplete/subcomponents/ScanDestination/ScanDestinationPhoneInput'
import { CountryType } from '../../util/CountryCodes'

export const getInputType = (type: ContactInfoFields): InputMode => {
  switch (type) {
    case ContactInfoFields.EMAIL:
      return InputMode.EMAIL
    case ContactInfoFields.TEXT:
      return InputMode.TEL
    case ContactInfoFields.CALL:
      return InputMode.TEL
    case ContactInfoFields.WEBSITE:
      return InputMode.URL
    default:
      return InputMode.TEXT
  }
}

export interface ContactInfoInputProps extends InputProps {
  boxShadow?: string
  mobile: boolean
  hideEmailIcon?: boolean
  label?: string
  countryCodeState?: any
}

export const ContactInfoInput: FC<ContactInfoInputProps> = ({
  inputId,
  type,
  onChange,
  value,
  errors,
  countryCodeState,
  label,
  handleCountryCodeChange
}) => {
  const contactType = type.toLowerCase()
  const isNumeric = contactType === 'call' || contactType === 'text'
  const isError = Boolean(indexInterfaceWithVar(errors, contactType))

  const IconComponent: FC = () => {
    if (contactType === ContactInfoFields.EMAIL) {
      return <MailOutlined />
    }

    if (contactType === ContactInfoFields.WEBSITE) {
      return <WebOutlined />
    }

    return undefined
  }

  const Component = () => {
    if (!countryCodeState)
      return (
        <CountryCodeAutocomplete
          onChange={(_, option) => {
            const countryCodeTyped = option.value as CountryType
            handleCountryCodeChange(
              type.toLowerCase() as 'text' | 'call',
              countryCodeTyped?.phone || ''
            )
          }}
        />
      )

    const [countryCode, setCountryCode] = countryCodeState[contactType === 'call' ? 'call' : 'text']

    return (
      <Autocomplete
        disablePortal
        defaultValue={DEFAULT_COUNTRY_CODE}
        value={countryCode}
        onChange={(_, v) => {
          if (v) {
            setCountryCode(v)
            handleCountryCodeChange(contactType as 'text' | 'call', v.phone)
          }
        }}
        options={COUNTRY_CODES}
        fullWidth
        getOptionLabel={option => `+${option.phone} ${option.code}`}
        renderInput={params => <TextField {...params} label="Country code" fullWidth hiddenLabel />}
      />
    )
  }

  return (
    <Stack direction="row" flexGrow={1}>
      {isNumeric ? (
        <Stack direction="row" flexGrow={1}>
          <Stack flex={`1 0 200px`} sx={{ maxWidth: '50%' }}>
            <Component />
          </Stack>
          <TextField
            data-testid={inputId}
            type={getInputType(type)}
            value={value}
            error={isError}
            helperText={indexInterfaceWithVar(errors, contactType)?.message}
            label={label || ''}
            onChange={onChange}
            fullWidth
            startAdornment={isNumeric ? undefined : <IconComponent />}
          />
        </Stack>
      ) : (
        <TextField
          data-testid={inputId}
          type={getInputType(type)}
          value={value}
          error={isError}
          helperText={indexInterfaceWithVar(errors, contactType)?.message}
          label={label || ''}
          onChange={onChange}
          fullWidth
          startAdornment={isNumeric ? undefined : <IconComponent />}
        />
      )}
    </Stack>
  )
}
